import { useState, useMemo } from 'react';
import { getTeamPlayerIds, getTournamentType } from '../../engine';
import { MatchFilterPill } from '../ui/MatchFilterPill';
import { PlayerSingleMatchRow } from './PlayerSingleMatchRow';
import { PlayerMatchRow } from './PlayerMatchRow';
import { useLocale } from '../../i18n';

export interface AllMatchesViewProps {
  /** The profile player whose matches are listed (win/loss perspective). */
  playerId: any;
  /** Tournament history rows ({ tournament, matches }). */
  history: any[];
  /** Single (non-tournament) matches the player took part in. */
  singleMatches?: any[];
  /** Manual ELO adjustments for the player, rendered inline in the timeline. */
  adjustments?: any[];
  /** Full player DB used to resolve names/ELO for the rows. */
  allPlayers?: any[];
}

/**
 * Player "all matches" tab — a chronological, format-filterable timeline of the
 * player's tournament matches, single matches and manual ELO adjustments.
 * Extracted verbatim from the monolith (legacy/App.jsx); markup/logic unchanged.
 * Leaf component — takes no Padel24Engine prop.
 */
export function AllMatchesView({
  playerId,
  history,
  singleMatches = [],
  adjustments = [],
  allPlayers = [],
}: AllMatchesViewProps) {
  // `tr`, not `t`: in this file `t` is the tournament, all the way down.
  const { t: tr, formatDate } = useLocale();
  const [formatFilter, setFormatFilter] = useState('all');

  // The series gender palette is the AA shade of each hue (#1295, as #1293
  // did for KM): white on it was 3.68 / 3.53 / 4.23:1, now 5.69 / 5.89 / 5.65.
  const GENDER_COLOR = { M: '#1961d3', F: '#c01074', X: '#7845df' };
  const GENDER_LABEL = {
    M: tr('players.allMatches.genderMen'),
    F: tr('players.allMatches.genderWomen'),
    X: tr('players.allMatches.genderMix'),
  };
  const playerGender = (allPlayers || []).find((p) => p.id === playerId)?.gender || null;
  // The class gender for a match (seriespill/KM): via classId, divisionId, pair
  // in t.pairs, pair nested in divisions, otherwise the player's own gender
  // (Damer/Herrer = the class gender). Returns 'M' | 'F' | 'X' | null.
  const genderForMatch = (t, m) => {
    const classes = t?.config?.classes || t?.classes || [];
    if (classes.length) {
      // deviation: explicit Map<any, any> annotation (the monolith is @ts-nocheck so
      // never typed this); without it TS infers Map<unknown, unknown> from the
      // any[]-tuple .map and flags `.get(...).gender`. Logic/markup unchanged.
      const byClassId = new Map<any, any>(classes.map((c) => [c.id, c]));
      const divToClass = new Map();
      classes.forEach((c) => (c.divisions || []).forEach((d) => divToClass.set(d.id, c)));
      if (m?.classId && byClassId.get(m.classId)) return byClassId.get(m.classId).gender;
      if (m?.divisionId && divToClass.get(m.divisionId)) return divToClass.get(m.divisionId).gender;
      const pid = m?.team1?.pairId || m?.team2?.pairId;
      if (pid) {
        const pr = (t?.pairs || []).find((p) => p.id === pid);
        if (pr?.classId && byClassId.get(pr.classId)) return byClassId.get(pr.classId).gender;
        if (pr?.divisionId && divToClass.get(pr.divisionId))
          return divToClass.get(pr.divisionId).gender;
        for (const c of classes) {
          for (const d of c.divisions || []) {
            if ((d.pairs || []).some((p) => p.id === pid)) return c.gender;
          }
        }
      }
    }
    return null;
  };
  const typeMetaFor = (t, m) => {
    // In a player profile the player's own gender drives the color (Damer=pink,
    // Herrer=blue). Purple is only shown when the class is actually Mix.
    const resolveG = () => {
      const cg = genderForMatch(t, m);
      return cg === 'X' ? 'X' : playerGender || cg;
    };
    if (t?.type === 'klubbmesterskap') {
      const g = resolveG();
      return {
        abbrev: 'KM',
        color: GENDER_COLOR[g] || '#f59e0b',
        label: 'Klubbmesterskap' + (g ? ` · ${GENDER_LABEL[g]}` : ''),
      };
    }
    if (t?.type === 'seriespill') {
      const g = resolveG();
      return {
        abbrev: 'Serie',
        color: GENDER_COLOR[g] || '#1961d3',
        label: 'Seriespill' + (g ? ` · ${GENDER_LABEL[g]}` : ''),
      };
    }
    // deviation: monolith calls `Padel24Engine.getTournamentType(...)` off the live
    // wiring object; that key is wired to the engine's `getTournamentType` export, so
    // this uses the named import directly (no Padel24Engine prop needed here).
    const tt = getTournamentType(t?.config);
    return {
      abbrev: tt?.abbrev || 'P24',
      color: tt?.color || '#94a3b8',
      label: tt?.label || tr('players.allMatches.tournamentFallback'),
    };
  };

  const items = useMemo(() => {
    const list = [];
    (history || []).forEach((h) => {
      const t = h.tournament;
      if (t?.isVirtual || t?.archiveId === 'virtual_single_matches') return;
      (h.matches || []).forEach((m) => {
        if (!m.completed) return;
        const meta = typeMetaFor(t, m);
        const [t1p1, t1p2] = getTeamPlayerIds(m.team1);
        const inT1 = t1p1 === playerId || t1p2 === playerId;
        const won = inT1 ? m.score1 > m.score2 : m.score2 > m.score1;
        const lost = inT1 ? m.score2 > m.score1 : m.score1 > m.score2;
        list.push({
          kind: 'tournament_match',
          timestamp: m.playedAt || m.completedAt || t.archivedAt || 0,
          match: m,
          tournament: t,
          typeKey: meta.abbrev,
          typeMeta: meta,
          result: won ? 'win' : lost ? 'loss' : 'draw',
        });
      });
    });
    (singleMatches || []).forEach((m) => {
      const inT1 = m.team1?.p1Id === playerId || m.team1?.p2Id === playerId;
      const won = (inT1 && m.result?.winner === 'team1') || (!inT1 && m.result?.winner === 'team2');
      const lost =
        (inT1 && m.result?.winner === 'team2') || (!inT1 && m.result?.winner === 'team1');
      list.push({
        kind: 'single_match',
        timestamp: m.playedAt || 0,
        match: m,
        typeKey: 'ENK',
        typeMeta: { abbrev: 'ENK', color: '#94a3b8', label: tr('players.allMatches.singleMatch') },
        result: won ? 'win' : lost ? 'loss' : 'draw',
      });
    });
    (adjustments || []).forEach((a) => {
      list.push({
        kind: 'adjustment',
        timestamp: a.adjustedAt || 0,
        adjustment: a,
        typeKey: 'JUST',
        typeMeta: { abbrev: '⚙', color: '#fbbf24', label: tr('players.allMatches.adjustment') },
        result: null,
      });
    });
    const rnd = (it) => {
      const r = it.match?.round;
      return typeof r === 'number' ? r : 0;
    };
    list.sort((a, b) => b.timestamp - a.timestamp || rnd(b) - rnd(a));
    return list;
    // deviation: monolith's dep array intentionally omits the locally-defined
    // `typeMetaFor` helper; preserved verbatim.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, singleMatches, adjustments, playerId]);

  const formats = useMemo(() => {
    const seen = new Map();
    items.forEach((it) => {
      if (it.kind !== 'adjustment') seen.set(it.typeKey, it.typeMeta);
    });
    return [...seen.entries()].map(([key, meta]) => ({ key, meta }));
  }, [items]);

  const matchCount = items.filter((it) => it.kind !== 'adjustment').length;
  const filtered = items.filter((it) => {
    if (formatFilter !== 'all' && it.typeKey !== formatFilter) return false;
    return true;
  });

  const dateStr = (ts) =>
    ts
      ? formatDate(new Date(ts), {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
      : '';

  if (items.length === 0) {
    return (
      <div className="card p-8 text-center text-sm text-ink-3 italic">
        {tr('players.allMatches.empty')}
      </div>
    );
  }

  return (
    <div>
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-1.5 mb-4">
        <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mr-1 w-14">
          {tr('players.allMatches.format')}
        </span>
        <MatchFilterPill active={formatFilter === 'all'} onClick={() => setFormatFilter('all')}>
          {tr('players.allMatches.all')}
        </MatchFilterPill>
        {formats.map((f) => (
          <MatchFilterPill
            key={f.key}
            active={formatFilter === f.key}
            onClick={() => setFormatFilter(f.key)}
          >
            {f.meta.abbrev}
          </MatchFilterPill>
        ))}
      </div>

      <div className="text-xs text-ink-3 mb-2">
        {tr('players.allMatches.count', { shown: filtered.length, total: matchCount })}
      </div>

      <div className="card overflow-hidden divide-y divide-line-soft">
        {filtered.length === 0 ? (
          <div className="p-6 text-center text-sm text-ink-3 italic">
            {tr('players.allMatches.noMatchesForFilter')}
          </div>
        ) : (
          filtered.map((it, i) => {
            if (it.kind === 'adjustment') {
              const a = it.adjustment;
              const up = (a.change || 0) > 0;
              return (
                <div
                  key={`adj-${a.id}`}
                  className="px-3 py-2 flex items-center gap-2"
                  style={{ background: up ? '#f0fdf4' : '#fffbeb' }}
                >
                  <span
                    className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0"
                    style={{
                      background: up ? '#bbf7d0' : '#fde68a',
                      color: up ? '#166534' : '#92400e',
                    }}
                  >
                    ⚙
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-ink">
                      {tr('players.allMatches.manualAdjustment')}{' '}
                      <span className="font-mono text-[10px] text-ink-3 ml-1">
                        {dateStr(it.timestamp)}
                      </span>
                    </div>
                    {a.reason && (
                      <div className="text-xs text-ink-2 italic truncate">"{a.reason}"</div>
                    )}
                  </div>
                  <span
                    className="font-mono text-sm font-bold flex-shrink-0"
                    style={{ color: up ? 'var(--lime-deep)' : '#e11d48' }}
                  >
                    {up ? '+' : ''}
                    {a.change}
                  </span>
                </div>
              );
            }
            return (
              <div key={i} className="flex items-stretch gap-1 pl-2">
                <span
                  className="font-mono text-[9px] font-bold px-1.5 py-1 rounded flex-shrink-0 self-start mt-2"
                  style={{ background: (it.typeMeta.color || '#94a3b8') + '40', color: '#0f1611' }}
                  title={`${it.typeMeta.label}${it.tournament ? ` · ${it.tournament.config?.name || ''}` : ''} · ${dateStr(it.timestamp)}`}
                >
                  {it.typeMeta.abbrev}
                </span>
                <div className="flex-1 min-w-0">
                  {it.kind === 'single_match' ? (
                    <PlayerSingleMatchRow
                      match={it.match}
                      playerId={playerId}
                      allPlayers={allPlayers}
                    />
                  ) : (
                    <PlayerMatchRow
                      match={it.match}
                      playerId={playerId}
                      players={it.tournament.players || []}
                      kmPairs={
                        it.tournament.type === 'klubbmesterskap' ||
                        it.tournament.type === 'seriespill'
                          ? it.tournament.pairs || []
                          : null
                      }
                    />
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
